import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Field } from "@/pages/Login";
import { Sparkles, User, Building2 } from "lucide-react";

export default function Register() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get("role") === "company" ? "company" : "candidate");
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "", ico: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (role === "company") {
      const ico = form.ico.trim();
      if (!ico) { toast.error("IČO firmy je povinné"); return; }
      if (!/^\d{8}$/.test(ico)) { toast.error("IČO musí obsahovať presne 8 číslic"); return; }
    }
    setBusy(true);
    try {
      await api.post("/auth/register", { ...form, role });
      nav("/pending");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="mx-auto w-full max-w-md flex-1 flex flex-col px-6 pt-16 pb-10">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-blue-700 flex items-center justify-center"><Sparkles className="text-white" size={20} /></div>
          <span className="font-head text-xl font-bold tracking-tight">Profeeluj<span className="text-blue-700"> AI</span></span>
        </div>
        <h1 className="mt-10 font-head text-3xl font-bold tracking-tight">Vytvorte si účet</h1>
        <p className="mt-2 text-slate-600">Kto ste?</p>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <RoleCard active={role === "candidate"} onClick={() => setRole("candidate")} icon={User} title="Kandidát" sub="Hľadám prácu" testId="role-candidate" />
          <RoleCard active={role === "company"} onClick={() => setRole("company")} icon={Building2} title="Firma" sub="Hľadám ľudí" testId="role-company" />
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field label={role === "company" ? "Vaše meno" : "Meno a priezvisko"} value={form.name} onChange={set("name")} testId="register-name" />
          {role === "company" && <Field label="Názov firmy" value={form.company_name} onChange={set("company_name")} testId="register-company" />}
          {role === "company" && <Field label="IČO firmy*" value={form.ico} onChange={(v) => set("ico")(v.replace(/\D/g, "").slice(0, 8))} testId="register-ico" />}
          <Field label="Email" type="email" value={form.email} onChange={set("email")} testId="register-email" />
          <Field label="Heslo" type="password" value={form.password} onChange={set("password")} testId="register-password" />
          <button data-testid="register-submit-btn" disabled={busy}
            className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-4 font-semibold transition-colors active:scale-95 disabled:opacity-60">
            {busy ? "Vytváram…" : "Vytvoriť účet"}
          </button>
        </form>
        <p className="mt-5 text-center text-sm text-slate-600">
          Už máte účet? <Link to="/login" className="font-semibold text-blue-700">Prihlásiť sa</Link>
        </p>
      </div>
    </div>
  );
}

function RoleCard({ active, onClick, icon: Icon, title, sub, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`flex flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-colors ${active ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${active ? "bg-blue-700" : "bg-slate-100"}`}>
        <Icon size={20} className={active ? "text-white" : "text-slate-500"} />
      </div>
      <div>
        <div className="font-head font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </div>
    </button>
  );
}
